import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import SignatureCanvas from '../components/SignatureCanvas';
import '../styles/FormPage.css';

const ClientForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savedClientId, setSavedClientId] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedSubServices, setSelectedSubServices] = useState({});

  const [formData, setFormData] = useState({
    // Basic Info (visible in header box)
    order_date: new Date().toISOString().split('T')[0],
    package_amount: '',

    // Client Information
    client_name: '',
    person_name: '',
    designation: '',
    address: '',
    phone: '',
    email: '',
    domains: '',
    gstin_no: '',

    // Location
    city: '',
    state: '',
    pincode: '',

    // Package Details (optional - used for package description)
    package: 'Digital Marketing Package',

    // GST Calculation (auto-calculated)
    gst_amount: '',
    total_amount: '', // package_amount + gst_amount

    // Service Details
    specific_guidelines: '',

    // SEO Details
    seo_keyword_range: '',
    seo_location: '',
    seo_keywords_list: '',

    // Google Adwords Details
    adwords_keywords: '',
    adwords_period: '',
    adwords_location: '',
    adwords_keywords_list: '',

    // Special Guidelines
    special_guidelines: '',

    // Payment
    payment_mode: '',

    // Signature
    signature_name: '',
    signature_designation: '',
    signature_text: '',
    esignature_data: '',
  });

  useEffect(() => {
    loadServices();
    if (isEditMode) {
      loadClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadServices = async () => {
    try {
      const response = await api.getServices(true, true);
      if (response.success) {
        setServices(response.data);
      }
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  };

  const loadClient = async () => {
    try {
      setLoading(true);
      const response = await api.getClient(id);
      const client = response.data;
      
      setFormData({
        order_date: client.order_date || new Date().toISOString().split('T')[0],
        package_amount: client.package_amount || '',
        client_name: client.client_name || '',
        person_name: client.person_name || '',
        designation: client.designation || '',
        address: client.address || '',
        phone: client.phone || '',
        email: client.email || '',
        domains: Array.isArray(client.domains) ? client.domains.join(', ') : (client.domains || ''),
        gstin_no: client.gstin_no || '',
        city: client.city || '',
        state: client.state || '',
        pincode: client.pincode || '',
        package: client.package || '',
        gst_amount: client.gst_amount || '',
        total_amount: client.total_amount || '',
        specific_guidelines: client.specific_guidelines || '',
        seo_keyword_range: client.seo_keyword_range || '',
        seo_location: client.seo_location || '',
        seo_keywords_list: client.seo_keywords_list || '',
        adwords_keywords: client.adwords_keywords || '',
        adwords_period: client.adwords_period || '',
        adwords_location: client.adwords_location || '',
        adwords_keywords_list: client.adwords_keywords_list || '',
        special_guidelines: client.special_guidelines || '',
        payment_mode: client.payment_mode || '',
        signature_name: client.signature_name || '',
        signature_designation: client.signature_designation || '',
        signature_text: client.signature_text || '',
        esignature_data: client.esignature_data || '',
      });

      // Load client services
      if (client.client_id) {
        try {
          const servicesResponse = await api.getClientServices(client.client_id);
          if (servicesResponse.success) {
            const serviceIds = servicesResponse.data.services.map(s => s.id);
            setSelectedServices(serviceIds);
            
            const subServicesMap = {};
            servicesResponse.data.sub_services.forEach(ss => {
              subServicesMap[ss.id] = {
                selected: true,
                quantity: ss.quantity || 1
              };
            });
            setSelectedSubServices(subServicesMap);
          }
        } catch (err) {
          console.error('Failed to load client services:', err);
        }
      }
    } catch (err) {
      setError('Failed to load client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate GST (18%) and total_amount
  useEffect(() => {
    const packageAmount = parseFloat(formData.package_amount) || 0;
    const gstAmount = (packageAmount * 0.18).toFixed(2);
    const totalAmount = (packageAmount + parseFloat(gstAmount)).toFixed(2);
    
    setFormData(prev => ({
      ...prev,
      gst_amount: gstAmount,
      total_amount: totalAmount
    }));
  }, [formData.package_amount]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleSubServiceToggle = (subServiceId, hasQuantity) => {
    setSelectedSubServices(prev => {
      const newState = { ...prev };
      if (newState[subServiceId]?.selected) {
        delete newState[subServiceId];
      } else {
        newState[subServiceId] = {
          selected: true,
          quantity: hasQuantity ? 1 : null
        };
      }
      return newState;
    });
  };

  const handleSubServiceQuantityChange = (subServiceId, quantity) => {
    setSelectedSubServices(prev => ({
      ...prev,
      [subServiceId]: {
        ...prev[subServiceId],
        quantity: parseInt(quantity) || 1
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const submitData = {
        ...formData,
        domains: formData.domains.split(',').map(d => d.trim()).filter(d => d),
        package_amount: parseFloat(formData.package_amount) || 0,
        gst_amount: parseFloat(formData.gst_amount) || 0,
        total_amount: parseFloat(formData.total_amount) || 0, // This is used by transactions table
      };

      let clientId;
      if (isEditMode) {
        await api.updateClient(id, submitData);
        clientId = id;
        setSuccess('Client updated successfully!');
      } else {
        const response = await api.createClient(submitData);
        clientId = response.data.client_id;
        setSuccess('Client created successfully!');
      }

      // Assign services to client
      const servicesData = {
        services: selectedServices,
        sub_services: Object.entries(selectedSubServices)
          .filter(([_, data]) => data.selected)
          .map(([id, data]) => ({
            id: parseInt(id),
            quantity: data.quantity || 1
          }))
      };

      if (selectedServices.length > 0 || Object.keys(selectedSubServices).length > 0) {
        await api.assignServicesToClient(clientId, servicesData);
      }

      // Store the saved client ID to show View/Print button
      setSavedClientId(clientId);
    } catch (err) {
      setError(err.message || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  if (loading && isEditMode) {
    return (
      <div className="form-page">
        <div className="form-container">
          <div className="loading-indicator">Loading client...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-container">
        <div className="form-header-section">
          <div className="form-title-group">
            <h1>{isEditMode ? 'Edit Client' : 'Add New Client'}</h1>
            <p className="form-subtitle">Complete client order form with services</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && (
          <div className="alert alert-success">
            {success}
            {savedClientId && (
              <div className="success-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button 
                  type="button"
                  onClick={() => navigate(`/clients/print/${savedClientId}`)}
                  className="btn btn-primary"
                >
                  📄 View/Print Order Form
                </button>
                <button 
                  type="button"
                  onClick={() => navigate('/clients')}
                  className="btn btn-secondary"
                >
                  Go to Clients List
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="client-order-form">
          
          {/* Order Details Section */}
          <div className="form-section">
            <h2 className="section-heading">Order Details</h2>
            {isEditMode && (
              <div className="info-display">
                <span className="info-label">Customer No:</span>
                <span className="info-value">{id}</span>
              </div>
            )}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="order_date">Order Date: *</label>
                <input
                  type="date"
                  id="order_date"
                  name="order_date"
                  value={formData.order_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="package_amount">Package Amount (₹): *</label>
                <input
                  type="number"
                  id="package_amount"
                  name="package_amount"
                  value={formData.package_amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="package">Package Description:</label>
                <input
                  type="text"
                  id="package"
                  name="package"
                  value={formData.package}
                  onChange={handleChange}
                  placeholder="e.g., Digital Marketing Package, SEO Package, etc."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group half-width">
                <label>GST (18%):</label>
                <input
                  type="text"
                  value={`₹${formData.gst_amount}`}
                  disabled
                  className="calculated-field"
                />
              </div>
              <div className="form-group half-width">
                <label>Total Amount (with GST):</label>
                <input
                  type="text"
                  value={`₹${formData.total_amount}`}
                  disabled
                  className="calculated-field"
                />
              </div>
            </div>
          </div>

          {/* Client Information Section */}
          <div className="form-section">
            <h2 className="section-heading">Client Information</h2>
            <div className="form-group">
              <label htmlFor="client_name">Company Name: *</label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="person_name">Contact Person: *</label>
                <input
                  type="text"
                  id="person_name"
                  name="person_name"
                  value={formData.person_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="designation">Designation:</label>
                <input
                  type="text"
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Address: *</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="phone">Contact No.: *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="email">E-mail: *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="domains">Domain Name(s):</label>
                <input
                  type="text"
                  id="domains"
                  name="domains"
                  value={formData.domains}
                  onChange={handleChange}
                  placeholder="example.com, example.net (comma-separated)"
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="gstin_no">GSTIN No.:</label>
                <input
                  type="text"
                  id="gstin_no"
                  name="gstin_no"
                  value={formData.gstin_no}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="city">City:</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="state">State:</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pincode">Pincode:</label>
              <input
                type="text"
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Services Section */}
          <div className="form-section">
            <h2 className="section-heading">Select Services</h2>
            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-column">
                  <div className="service-category">{service.service_name}</div>
                  <div className="checkbox-list">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedServices.includes(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                      />
                      <span>{service.service_name}</span>
                    </label>
                    
                    {service.sub_services && service.sub_services.map(subService => (
                      <label key={subService.id} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={selectedSubServices[subService.id]?.selected || false}
                          onChange={() => handleSubServiceToggle(subService.id, subService.has_quantity === 1)}
                        />
                        <span>{subService.sub_service_name}</span>
                        {subService.has_quantity === 1 && selectedSubServices[subService.id]?.selected && (
                          <input
                            type="number"
                            min="1"
                            value={selectedSubServices[subService.id]?.quantity || 1}
                            onChange={(e) => handleSubServiceQuantityChange(subService.id, e.target.value)}
                            placeholder={subService.quantity_label || 'Qty'}
                            className="quantity-input"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Guidelines Section */}
          <div className="form-section">
            <h2 className="section-heading">Specific Guidelines</h2>
            <div className="form-group">
              <label htmlFor="specific_guidelines">Website & SEO Guidelines:</label>
              <textarea
                id="specific_guidelines"
                name="specific_guidelines"
                value={formData.specific_guidelines}
                onChange={handleChange}
                rows="5"
              />
            </div>
          </div>

          {/* SEO Details Section */}
          <div className="form-section">
            <h2 className="section-heading">SEO Details</h2>
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="seo_keyword_range">Keyword Range:</label>
                <select
                  id="seo_keyword_range"
                  name="seo_keyword_range"
                  value={formData.seo_keyword_range}
                  onChange={handleChange}
                >
                  <option value="">Select Range</option>
                  <option value="upto-25">Up to 25</option>
                  <option value="25-50">25 - 50</option>
                  <option value="75-100">75 - 100</option>
                </select>
              </div>
              <div className="form-group half-width">
                <label htmlFor="seo_location">Location:</label>
                <select
                  id="seo_location"
                  name="seo_location"
                  value={formData.seo_location}
                  onChange={handleChange}
                >
                  <option value="">Select Location</option>
                  <option value="state">State</option>
                  <option value="india">India</option>
                  <option value="country">Country</option>
                  <option value="global">Global</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="seo_keywords_list">Top Products / Keywords:</label>
              <textarea
                id="seo_keywords_list"
                name="seo_keywords_list"
                value={formData.seo_keywords_list}
                onChange={handleChange}
                rows="8"
                placeholder="1. Keyword one&#10;2. Keyword two&#10;3. Keyword three&#10;..."
              />
            </div>
          </div>

          {/* Google Adwords Section */}
          <div className="form-section">
            <h2 className="section-heading">Google Adwords</h2>
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="adwords_keywords">Number of Keywords:</label>
                <input
                  type="text"
                  id="adwords_keywords"
                  name="adwords_keywords"
                  value={formData.adwords_keywords}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="adwords_period">Period:</label>
                <input
                  type="text"
                  id="adwords_period"
                  name="adwords_period"
                  value={formData.adwords_period}
                  onChange={handleChange}
                  placeholder="Monthly / Quarterly"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="adwords_location">Location:</label>
              <input
                type="text"
                id="adwords_location"
                name="adwords_location"
                value={formData.adwords_location}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="adwords_keywords_list">Keywords List:</label>
              <textarea
                id="adwords_keywords_list"
                name="adwords_keywords_list"
                value={formData.adwords_keywords_list}
                onChange={handleChange}
                rows="4"
              />
            </div>
          </div>

          {/* Special Guidelines Section */}
          <div className="form-section">
            <h2 className="section-heading">Special Guidelines</h2>
            <div className="form-group">
              <textarea
                name="special_guidelines"
                value={formData.special_guidelines}
                onChange={handleChange}
                rows="5"
              />
            </div>
          </div>

          {/* Payment Mode Section */}
          <div className="form-section">
            <h2 className="section-heading">Mode of Payment</h2>
            <div className="payment-options">
              {['cheque', 'neft', 'rtgs', 'credit-debit-card', 'upi'].map(mode => (
                <label key={mode} className="checkbox-item">
                  <input
                    type="radio"
                    name="payment_mode"
                    value={mode}
                    checked={formData.payment_mode === mode}
                    onChange={handleChange}
                  />
                  <span>{mode.replace(/-/g, ' ').toUpperCase()}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Signature Section */}
          <div className="form-section">
            <h2 className="section-heading">Signature</h2>
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="signature_name">Name & Designation:</label>
                <input
                  type="text"
                  id="signature_name"
                  name="signature_name"
                  value={formData.signature_name}
                  onChange={handleChange}
                  placeholder="Full name and designation"
                />
              </div>
              <div className="form-group half-width">
                <label htmlFor="signature_text">Typed Signature (Optional):</label>
                <input
                  type="text"
                  id="signature_text"
                  name="signature_text"
                  value={formData.signature_text}
                  onChange={handleChange}
                  placeholder="Type your name"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Digital Signature (Draw with mouse or touch):</label>
              <SignatureCanvas
                value={formData.esignature_data}
                onChange={(dataUrl) => setFormData(prev => ({ ...prev, esignature_data: dataUrl }))}
                disabled={loading}
              />
              <p className="field-hint">
                Your signature will be encrypted and stored securely. It will appear on the printed order form.
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
